import React from "react";

import { cn } from "../utils/cn";

export interface SimFrameProps {
    /**
     * Fixed frame height in px. Set this to the block's max height from
     * {@link SIM_BLOCK_HEIGHTS} so the looping sim's content (build → fade →
     * rebuild) changes INSIDE a stable box — the surrounding page never
     * oscillates height (no scroll hijack) and the embed keeps a clean edge.
     * Omit to let the frame grow to content (not recommended for looping
     * blocks).
     */
    height?: number;
    /** Extra classes on the frame element — e.g. a `max-w-*`, margins. */
    className?: string;
    children: React.ReactNode;
}

/**
 * SimFrame — a thin-bordered, fixed-height wrapper that "frames" a sim block.
 * The low-level MECHANISM + the manual/custom-height escape hatch.
 *
 * A sim block's rendered height varies across its auto-play loop. Wrapping it
 * in a frame sized to its MAX height ({@link SIM_BLOCK_HEIGHTS}) both stops the
 * page from jumping as the animation plays and gives the embed a clean,
 * consistent border. Pair the `height` with the same reference width the block
 * expects (see `sim/IMPORT.md` → Framing).
 *
 * **The ergonomic default is the `framed` prop each program/neobank sim block
 * carries** — the block owns its height, so the consumer passes no pixel value
 * (`<NeobankProgramsOutcomeSim framed />`). Reach for `SimFrame` directly only
 * when you need a custom height, or to frame a block that has no `framed` prop
 * (the Buy / Trade / Enable / Positions widgets):
 *
 * ```tsx
 * import {
 *   SimFrame,
 *   SIM_BLOCK_HEIGHTS,
 *   NeobankProgramsOutcomeSim
 * } from "@solomon-apps/shared/sim";
 *
 * // Equivalent to `<NeobankProgramsOutcomeSim framed />`, done by hand.
 * <SimFrame height={SIM_BLOCK_HEIGHTS.NeobankProgramsOutcomeSim}>
 *   <NeobankProgramsOutcomeSim />
 * </SimFrame>
 * ```
 *
 * The border (`border-line-base`) + surface (`bg-surface-ground`) tokens are
 * baked into the sim stylesheet, so a consumer needs no extra styling.
 */
export function SimFrame({
    height,
    className,
    children
}: SimFrameProps): React.ReactElement {
    return (
        <div
            className={cn(
                "overflow-hidden border border-line-base bg-surface-ground",
                className
            )}
            style={height !== undefined ? { height } : undefined}
        >
            {children}
        </div>
    );
}
